import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert, Avatar, Box, Chip, CircularProgress, Divider, IconButton, InputAdornment,
  Paper, Stack, TextField, Tooltip, Typography,
} from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import SearchIcon from '@mui/icons-material/Search';
import RefreshIcon from '@mui/icons-material/Refresh';
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import LanguageIcon from '@mui/icons-material/Language';
import PhoneIcon from '@mui/icons-material/Phone';
import BugReportOutlinedIcon from '@mui/icons-material/BugReportOutlined';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { getConversationMessageRole, getInteractionPresentation, getMessageExchange, getSelectedChoice } from '../utils/conversationLogPresentation';

/** Purpose: Perform format date for the insight conversations module. Why: Callers need a consistent result from this shared operation. */
const formatDate = (value) => value ? new Date(value).toLocaleString() : '—';
const WHATSAPP_DELIVERY_LABELS = {
  sending: 'Sending',
  sent: 'Sent',
  delivered: 'Delivered',
  read: 'Read',
  failed: 'Failed',
};
/** Purpose: Perform stringify for the insight conversations module. Why: Callers need a consistent result from this shared operation. */
const stringify = (value) => {
  if (value === undefined || value === null || value === '') return '—';
  if (typeof value === 'string') {
    try { return JSON.stringify(JSON.parse(value), null, 2); } catch (_) { return value; }
  }
  try { return JSON.stringify(value, null, 2); } catch (_) { return String(value); }
};

/** Purpose: Resolve channel icon for the insight conversations module. Why: The surrounding operation uses this value to make its decision. */
const ChannelIcon = ({ channel }) => {
  if (channel === 'whatsapp') return <WhatsAppIcon fontSize="small" />;
  if (channel === 'voice') return <PhoneIcon fontSize="small" />;
  return <LanguageIcon fontSize="small" />;
};

/** Purpose: inspect conversation transcripts and extracted insights. Why: teams need to understand user interactions beyond aggregate dashboard metrics. */
export default function InsightConversations() {
  const theme = useTheme();
  const { selectedBot, selectedEnv } = useAuth();
  const [search, setSearch] = useState('');
  const [conversations, setConversations] = useState([]);
  const [selectedConversation, setSelectedConversation] = useState(null);
  const [messages, setMessages] = useState([]);
  const [selectedMessage, setSelectedMessage] = useState(null);
  const [logData, setLogData] = useState(null);
  const [loadingList, setLoadingList] = useState(false);
  const [loadingTranscript, setLoadingTranscript] = useState(false);
  const [loadingLogs, setLoadingLogs] = useState(false);
  const [error, setError] = useState('');
  const selectedStepRef = useRef(null);

  useEffect(() => {
    if (!loadingLogs && logData?.selectedStepArrayIndex >= 0 && selectedStepRef.current) {
      selectedStepRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }, [loadingLogs, logData]);

  /** Purpose: Build panel sx within the insight conversations module. Why: This keeps the surrounding operation consistent with the current interface state and data shape. */
  const panelSx = useMemo(() => ({
    minWidth: 0,
    minHeight: 0,
    borderRadius: 3,
    border: `1px solid ${alpha(theme.palette.divider, 0.85)}`,
    bgcolor: theme.palette.mode === 'dark' ? alpha('#0f172a', 0.92) : alpha('#fff', 0.96),
    overflow: 'hidden',
  }), [theme]);

  const messageExchange = useMemo(
    () => getMessageExchange(messages, selectedMessage),
    [messages, selectedMessage]
  );
  const selectedChoice = useMemo(
    () => getSelectedChoice(logData?.session?.steps || [], selectedMessage),
    [logData, selectedMessage]
  );

  /** Purpose: Load load conversations for the insight conversations module. Why: The selected view needs current data to render or continue processing. */
  const loadConversations = useCallback(async () => {
    if (!selectedBot) return;
    setLoadingList(true);
    setError('');
    try {
      const response = await axios.get('/api/v1/analytics/conversations', {
        params: { botId: selectedBot, envName: selectedEnv || 'dev', search },
      });
      const items = response.data.conversations || [];
      setConversations(items);
      setSelectedConversation((current) => items.find((item) => item._id === current?._id) || items[0] || null);
    } catch (err) {
      setError(err.response?.data?.error || 'Conversations load nahi ho paayi.');
    } finally {
      setLoadingList(false);
    }
  }, [selectedBot, selectedEnv, search]);

  useEffect(() => {
    const timer = setTimeout(loadConversations, 250);
    return () => clearTimeout(timer);
  }, [loadConversations]);

  useEffect(() => {
    if (!selectedConversation?._id) {
      setMessages([]);
      setSelectedMessage(null);
      setLogData(null);
      return;
    }
    let active = true;
    setLoadingTranscript(true);
    setSelectedMessage(null);
    setLogData(null);
    axios.get(`/api/v1/analytics/conversations/${selectedConversation._id}/messages`, {
      params: { botId: selectedBot, envName: selectedEnv || 'dev' },
    }).then((response) => {
      if (active) setMessages(response.data.messages || []);
    }).catch((err) => {
      if (active) setError(err.response?.data?.error || 'Failed to load the transcript.');
    }).finally(() => { if (active) setLoadingTranscript(false); });
    return () => { active = false; };
  }, [selectedConversation, selectedBot, selectedEnv]);

  // Delivery receipts arrive asynchronously; refresh the selected transcript so
  // the bot's Sending/Sent/Delivered/Read/Failed state stays current on screen.
  useEffect(() => {
    if (!selectedConversation?._id || !selectedBot) return undefined;
    let active = true;
    const refreshTranscript = async () => {
      try {
        const response = await axios.get(`/api/v1/analytics/conversations/${selectedConversation._id}/messages`, {
          params: { botId: selectedBot, envName: selectedEnv || 'dev' },
        });
        if (active) setMessages(response.data.messages || []);
      } catch (_) {
        // Keep the last loaded transcript visible if a background refresh fails.
      }
    };
    const timer = window.setInterval(refreshTranscript, 5000);
    return () => {
      active = false;
      window.clearInterval(timer);
    };
  }, [selectedConversation?._id, selectedBot, selectedEnv]);

  /** Purpose: Handle open message logs in the insight conversations module. Why: Keeping this action in one handler makes its state or request update consistent. */
  const openMessageLogs = async (message) => {
    setSelectedMessage(message);
    setLoadingLogs(true);
    setLogData(null);
    try {
      const response = await axios.get(`/api/v1/analytics/messages/${message._id}/logs`, {
        params: { botId: selectedBot, envName: selectedEnv || 'dev' },
      });
      setLogData(response.data);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to load message logs.');
    } finally {
      setLoadingLogs(false);
    }
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, height: { lg: 'calc(100vh - 96px)' }, minHeight: 620 }}>
      {error && <Alert severity="error" onClose={() => setError('')}>{error}</Alert>}

      <Box sx={{ display: 'grid', flex: 1, minHeight: 0, gap: 1.25, gridTemplateColumns: { xs: '1fr', lg: '300px minmax(360px, 1fr) minmax(330px, 0.9fr)' } }}>
        <Paper sx={{ ...panelSx, display: 'flex', flexDirection: 'column' }}>
          <Box sx={{ p: 1.5 }}>
            <Stack direction="row" alignItems="center" justifyContent="space-between" mb={1}>
              <Typography variant="h6" fontWeight={750}>Visited users</Typography>
              <Tooltip title="Refresh"><IconButton size="small" onClick={loadConversations}><RefreshIcon fontSize="small" /></IconButton></Tooltip>
            </Stack>
            <TextField fullWidth size="small" value={search} onChange={(event) => setSearch(event.target.value)} placeholder="Name, number or message" InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment> }} />
          </Box>
          <Divider />
          <Box sx={{ flex: 1, minHeight: 0, overflowY: 'auto' }}>
            {loadingList ? <Box p={4} textAlign="center"><CircularProgress size={24} /></Box> : conversations.map((conversation) => {
              const active = selectedConversation?._id === conversation._id;
              const name = conversation.displayName || conversation.channelUserId || conversation.userId || 'Visitor';
              return (
                <Box key={conversation._id} onClick={() => setSelectedConversation(conversation)} sx={{ p: 1.5, cursor: 'pointer', borderBottom: `1px solid ${alpha(theme.palette.divider, 0.7)}`, bgcolor: active ? alpha(theme.palette.primary.main, 0.1) : 'transparent', '&:hover': { bgcolor: alpha(theme.palette.primary.main, 0.07) } }}>
                  <Stack direction="row" spacing={1.25} alignItems="center">
                    <Avatar sx={{ width: 36, height: 36, bgcolor: active ? 'primary.main' : 'action.selected' }}><PersonOutlineIcon fontSize="small" /></Avatar>
                    <Box minWidth={0} flex={1}>
                      <Stack direction="row" alignItems="center" spacing={0.5}><ChannelIcon channel={conversation.channel} /><Typography fontWeight={700} noWrap>{name}</Typography></Stack>
                      <Typography variant="body2" color="text.secondary" noWrap>{conversation.lastMessage || 'No message'}</Typography>
                      <Typography variant="caption" color="text.disabled">{formatDate(conversation.lastMessageAt)}</Typography>
                    </Box>
                  </Stack>
                </Box>
              );
            })}
            {!loadingList && !conversations.length && <Typography color="text.secondary" p={3}>No visitors found.</Typography>}
          </Box>
        </Paper>

        <Paper sx={{ ...panelSx, display: 'flex', flexDirection: 'column' }}>
          <Box sx={{ p: 1.5, borderBottom: `1px solid ${alpha(theme.palette.divider, 0.7)}` }}>
            <Typography variant="h6" fontWeight={750}>{selectedConversation?.displayName || selectedConversation?.channelUserId || 'Chat transcript'}</Typography>
            <Typography variant="caption" color="text.secondary">Select a message to view its logs in the panel on the right.</Typography>
          </Box>
          <Box sx={{ flex: 1, minHeight: 0, overflowY: 'auto', p: 2, bgcolor: theme.palette.mode === 'dark' ? alpha('#020617', 0.42) : alpha('#f8fafc', 0.8) }}>
            {loadingTranscript ? <Box p={4} textAlign="center"><CircularProgress size={25} /></Box> : messages.map((message) => {
              const role = getConversationMessageRole(message);
              const outgoing = role === 'bot';
              const active = selectedMessage?._id === message._id;
              const deliveryStatus = outgoing && message.channel === 'whatsapp'
                ? WHATSAPP_DELIVERY_LABELS[String(message.status || '').toLowerCase()]
                : '';
              const whatsappInteractive = message.channel === 'whatsapp' ? message.metadata?.whatsappInteractive : null;
              const interactiveOptions = [
                ...(whatsappInteractive?.buttons || []),
                ...(whatsappInteractive?.sections || []).flatMap((section) => section.rows || []),
              ];
              return (
                <Box key={message._id} display="flex" justifyContent={outgoing ? 'flex-end' : 'flex-start'} mb={1.5}>
                  <Box onClick={() => openMessageLogs(message)} sx={{ maxWidth: '82%', minWidth: 120, cursor: 'pointer', px: 1.5, py: 1.1, borderRadius: outgoing ? '18px 18px 4px 18px' : '18px 18px 18px 4px', bgcolor: outgoing ? 'primary.main' : 'background.paper', color: outgoing ? 'primary.contrastText' : 'text.primary', border: active ? `2px solid ${theme.palette.warning.main}` : `1px solid ${alpha(theme.palette.divider, 0.7)}`, boxShadow: '0 4px 14px rgba(15,23,42,0.08)' }}>
                    <Typography variant="caption" sx={{ opacity: 0.72 }}>{message.sender === 'agent' ? 'Agent' : outgoing ? 'Bot' : 'User'}{message.senderName && message.senderName !== message.sender ? ` · ${message.senderName}` : ''}</Typography>
                    <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' }}>{message.message || (message.type === 'audio' ? 'Audio message' : 'Empty message')}</Typography>
                    {interactiveOptions.length > 0 && (
                      <Box sx={{ mt: 1, pt: 0.75, borderTop: `1px solid ${alpha(outgoing ? theme.palette.primary.contrastText : theme.palette.divider, 0.22)}` }}>
                        <Typography variant="caption" sx={{ opacity: 0.78 }}>
                          WhatsApp {whatsappInteractive.type === 'list' ? 'list options' : 'quick replies'}
                          {whatsappInteractive.buttonText ? ` · ${whatsappInteractive.buttonText}` : ''}
                        </Typography>
                        <Stack spacing={0.4} mt={0.5}>
                          {interactiveOptions.map((option, index) => (
                            <Box key={`${option.id}-${index}`} sx={{ px: 0.75, py: 0.5, borderRadius: 1, bgcolor: alpha(outgoing ? theme.palette.primary.contrastText : theme.palette.text.primary, 0.08) }}>
                              <Typography variant="body2" sx={{ overflowWrap: 'anywhere' }}>{option.title || option.label || option.id}</Typography>
                              <Typography variant="caption" sx={{ opacity: 0.72, fontFamily: 'monospace', overflowWrap: 'anywhere' }}>ID: {option.id}</Typography>
                              {option.description ? <Typography variant="caption" display="block" sx={{ opacity: 0.78 }}>{option.description}</Typography> : null}
                            </Box>
                          ))}
                        </Stack>
                      </Box>
                    )}
                    <Stack direction="row" spacing={0.75} justifyContent="flex-end" alignItems="center" mt={0.5}>
                      {deliveryStatus && <Chip size="small" label={deliveryStatus} color={deliveryStatus === 'Failed' ? 'error' : ['Delivered', 'Read'].includes(deliveryStatus) ? 'success' : 'default'} variant="outlined" sx={{ height: 20, '& .MuiChip-label': { px: 0.75, fontSize: 10 } }} />}
                      <Typography variant="caption" sx={{ opacity: 0.7 }}>{formatDate(message.timestamp)}</Typography>
                    </Stack>
                  </Box>
                </Box>
              );
            })}
            {!loadingTranscript && selectedConversation && !messages.length && <Typography color="text.secondary">This visitor has no messages yet.</Typography>}
            {!selectedConversation && <Typography color="text.secondary">Select a visitor to view their transcript.</Typography>}
          </Box>
        </Paper>

        <Paper sx={{ ...panelSx, display: 'flex', flexDirection: 'column' }}>
          <Box sx={{ p: 1.5, borderBottom: `1px solid ${alpha(theme.palette.divider, 0.7)}` }}>
            <Stack direction="row" spacing={1} alignItems="center"><BugReportOutlinedIcon color="primary" /><Box><Typography variant="h6" fontWeight={750}>Message logs</Typography><Typography variant="caption" color="text.secondary">Flow steps, variables and provider calls</Typography></Box></Stack>
          </Box>
          <Box sx={{ flex: 1, minHeight: 0, overflowY: 'auto', p: 1.5 }}>
            {loadingLogs && <Box p={4} textAlign="center"><CircularProgress size={25} /></Box>}
            {!loadingLogs && !selectedMessage && <Alert severity="info">Select a message in the transcript to view its logs.</Alert>}
            {!loadingLogs && selectedMessage && logData && (
              <Stack spacing={1.5}>
                <Box>
                  <Stack direction="row" justifyContent="space-between" alignItems="center" mb={1}>
                    <Typography fontWeight={700}>Message exchange</Typography>
                    {selectedChoice ? <Chip size="small" color="primary" variant="outlined" label={`Selected option · ${selectedChoice}`} /> : null}
                  </Stack>
                  <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 1 }}>
                    {[
                      { label: 'User response', message: messageExchange.userMessage },
                      { label: 'Bot response', message: messageExchange.botMessage },
                    ].map(({ label, message }) => (
                      <Paper key={label} variant="outlined" sx={{ p: 1.25, minWidth: 0, bgcolor: label === 'Bot response' ? alpha(theme.palette.primary.main, 0.055) : 'background.paper', borderColor: label === 'Bot response' ? alpha(theme.palette.primary.main, 0.25) : 'divider' }}>
                        <Typography variant="overline" color={label === 'Bot response' ? 'primary.main' : 'text.secondary'}>{label}</Typography>
                        <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' }}>{message?.message || (label === 'Bot response' ? 'No bot reply recorded for this turn.' : 'No paired user message found.')}</Typography>
                        {message?.timestamp ? <Typography variant="caption" color="text.secondary">{formatDate(message.timestamp)}</Typography> : null}
                        {label === 'Bot response' && message?.channel === 'whatsapp' && message?.status ? <Chip size="small" sx={{ mt: 0.75 }} label={`WhatsApp · ${WHATSAPP_DELIVERY_LABELS[String(message.status).toLowerCase()] || message.status}`} color={message.status === 'failed' ? 'error' : ['delivered', 'read'].includes(String(message.status).toLowerCase()) ? 'success' : 'default'} variant="outlined" /> : null}
                      </Paper>
                    ))}
                  </Box>
                </Box>
                <Divider />
                <Box><Typography fontWeight={700}>Session</Typography><Typography variant="caption" color="text.secondary">{logData.session?.sessionKey || 'No linked session'}</Typography>{logData.session && <pre style={{ whiteSpace: 'pre-wrap', overflowWrap: 'anywhere', fontSize: 11 }}>{stringify({ traceId: logData.session.traceId, flowId: logData.session.flowId, currentNodeId: logData.session.currentNodeId, ended: logData.session.ended })}</pre>}</Box>
                <Box>
                  <Stack direction="row" justifyContent="space-between" alignItems="center"><Typography fontWeight={700}>Flow steps ({logData.session?.steps?.length || 0})</Typography>{logData.selectedStepIndex !== null && logData.selectedStepIndex !== undefined ? <Chip size="small" color="warning" label={`Focused #${logData.selectedStepIndex}`} /> : null}</Stack>
                  {(logData.session?.steps || []).map((step, index) => {
                    const selected = step.relation === 'selected';
                    const relationLabel = selected ? 'SELECTED MESSAGE' : step.relation === 'previous' ? 'PREVIOUS' : step.relation === 'next' ? 'NEXT' : 'UNLINKED';
                    return <Paper ref={selected ? selectedStepRef : null} variant="outlined" key={`${step.stepIndex}-${index}`} sx={{ p: 1, mt: 0.75, borderWidth: selected ? 2 : 1, borderColor: selected ? 'warning.main' : 'divider', bgcolor: selected ? alpha(theme.palette.warning.main, 0.12) : step.relation === 'previous' ? alpha(theme.palette.text.primary, 0.025) : 'background.paper', boxShadow: selected ? `0 0 0 3px ${alpha(theme.palette.warning.main, 0.12)}` : 'none', opacity: step.relation === 'previous' ? 0.78 : 1 }}>
                      <Stack direction="row" justifyContent="space-between" spacing={1}><Typography variant="caption" fontWeight={700}>#{step.stepIndex} {step.nodeLabel || step.nodeId || step.nodeType}</Typography><Chip size="small" color={selected ? 'warning' : 'default'} variant={selected ? 'filled' : 'outlined'} label={relationLabel} /></Stack>
                      <pre style={{ whiteSpace: 'pre-wrap', overflowWrap: 'anywhere', fontSize: 10, marginBottom: 0 }}>{stringify(step)}</pre>
                    </Paper>;
                  })}
                </Box>
                <Box><Typography fontWeight={700}>Execution logs ({logData.session?.logs?.length || 0})</Typography>{(logData.session?.logs || []).map((log, index) => <Box key={index} sx={{ mt: .75, p: 1, borderRadius: 2, border: '1px solid', borderWidth: log.relation === 'selected' ? 2 : 1, borderColor: log.relation === 'selected' ? 'warning.main' : 'divider', bgcolor: log.relation === 'selected' ? alpha(theme.palette.warning.main, .12) : alpha(theme.palette.text.primary, .04), opacity: log.relation === 'previous' ? .78 : 1 }}><Stack direction="row" justifyContent="flex-end"><Chip size="small" color={log.relation === 'selected' ? 'warning' : 'default'} variant={log.relation === 'selected' ? 'filled' : 'outlined'} label={log.relation === 'selected' ? 'SELECTED MESSAGE' : String(log.relation || 'unlinked').toUpperCase()} /></Stack><pre style={{ whiteSpace: 'pre-wrap', overflowWrap: 'anywhere', fontSize: 10, margin: 0 }}>{stringify(log)}</pre></Box>)}</Box>
                <Box>
                  <Typography fontWeight={700}>WhatsApp activity ({logData.interactions?.length || 0})</Typography>
                  {(logData.interactions || []).map((log, index) => {
                    const summary = getInteractionPresentation(log);
                    return (
                      <Paper variant="outlined" key={log._id || `${log.event}-${index}`} sx={{ p: 1, mt: 0.75 }}>
                        <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={1}>
                          <Typography variant="body2" fontWeight={700}>{summary.title}</Typography>
                          <Chip size="small" color={summary.outcome === 'Failed' ? 'error' : ['Delivered', 'Read'].includes(summary.outcome) ? 'success' : 'default'} label={summary.outcome} />
                        </Stack>
                        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' }}>{summary.detail}</Typography>
                        <Typography variant="caption" color="text.disabled">{log.direction} · {formatDate(log.createdAt)}</Typography>
                      </Paper>
                    );
                  })}
                  {!logData.interactions?.length && <Typography variant="body2" color="text.secondary" sx={{ mt: 0.75 }}>No provider activity is linked to this message.</Typography>}
                </Box>
              </Stack>
            )}
          </Box>
        </Paper>
      </Box>
    </Box>
  );
}
